/**
 * Format Aircraft Label Utility
 *
 * Provides utilities for enriching detection results with building/unit names
 * and formatting position coordinates for display in aircraft labels.
 */

import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'
import type { HangarDetectionResult } from './hangar-detection'
import type { PositionType } from '@/types/allocations'

/**
 * Detection result enriched with human-readable names
 */
export interface DetectionWithNames {
  areaId: string | null
  areaName: string
  unitId: string | null
  unitCode: string
  positionType: PositionType
  position: [number, number, number]
}

/**
 * Look up building and unit names from IDs
 *
 * @param detection - Detection result with IDs
 * @param position - Aircraft position [x, y, z] in feet
 * @param buildings - Array of buildings with generatedUnits
 * @returns Detection enriched with human-readable names
 */
export function enrichDetectionWithNames(
  detection: Pick<HangarDetectionResult, 'areaId' | 'unitId' | 'positionType'>,
  position: [number, number, number],
  buildings: Building3DConfigExtended[]
): DetectionWithNames {
  let areaName = 'Ramp'
  let unitCode = '—'

  if (detection.areaId) {
    const building = buildings.find((b) => b.id === detection.areaId)
    areaName = building?.name || `Building ${detection.areaId.slice(0, 8)}`

    if (detection.unitId && building?.generatedUnits) {
      const unit = building.generatedUnits.find((u) => u.id === detection.unitId)
      unitCode = unit?.code || `Unit ${detection.unitId.slice(0, 8)}`
    }
  }

  return {
    areaId: detection.areaId,
    areaName,
    unitId: detection.unitId,
    unitCode,
    positionType: detection.positionType,
    position,
  }
}

/**
 * Format position coordinates for display
 *
 * @param position - Position [x, y, z] in feet
 * @returns Formatted string like "(45.2, 120.5)"
 */
export function formatPosition(position: [number, number, number]): string {
  return `(${position[0].toFixed(1)}, ${position[2].toFixed(1)})`
}
